/**
 * API Service
 *
 * Central service for all API calls to the backend.
 * Uses axios for HTTP requests with automatic error handling.
 */

import type { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

/**
 * API Response wrapper
 */
export type ApiResponse<T = unknown> = {
  readonly data: T;
  readonly message?: string;
  readonly success: boolean;
};

/**
 * API Error response
 */

/**
 *
 */
export type ApiError = {
  readonly message: string;
  readonly status?: number;
  readonly errors?: Record<string, unknown>;
};

/**
 * Menu Item interface
 */
export type MenuItem = {
  readonly _id: string;
  readonly label: string;
  readonly slug: string;
  readonly order: number;
  readonly enabled: boolean;
  readonly hasSubmenu: boolean;
};

/**
 * Page Content interface
 */
export type PageContent = {
  readonly _id: string;
  readonly slug: string;
  readonly type: 'text-cloud' | 'image-gallery' | 'icon-ring' | 'shop-gallery' | 'blog';
  readonly content: Record<string, unknown>;
  readonly metadata?: {
    readonly title?: string;
    readonly description?: string;
  };
};

/**
 * Category interface
 * Categories are only used for image galleries (portfolio and shop)
 */
export type Category = {
  readonly _id: string;
  readonly name: string;
  readonly slug: string;
  readonly type: 'portfolio' | 'shop';
  readonly description?: string;
  readonly order: number;
  readonly enabled: boolean;
  readonly createdAt?: string;
  readonly updatedAt?: string;
};

/**
 * Shop Data interface
 */
export type ShopData = {
  readonly title?: string;
  readonly price?: number;
  readonly currency?: string;
  readonly purchaseUrl?: string;
  readonly stockStatus?: 'in_stock' | 'out_of_stock' | 'limited';
  readonly shopCategory?: string;
};

/**
 * Image interface
 */
export type Image = {
  readonly _id: string;
  readonly filename: string;
  readonly originalName: string;
  readonly url: string;
  readonly thumbnailUrl: string;
  readonly mediumUrl: string;
  readonly category: string;
  readonly alt?: string;
  readonly title?: string;
  readonly description?: string;
  readonly price?: number;
  readonly order: number;
  readonly shopData?: ShopData;
};

/**
 * Login credentials
 */
export type LoginCredentials = {
  readonly username: string;
  readonly password: string;
};

/**
 * Login response
 */
export type LoginResponse = {
  readonly success: boolean;
  readonly user: {
    readonly id: string;
    readonly username: string;
  };
};

/**
 * Site Settings interface
 */
export type SiteSettings = {
  readonly artistPhotoUrl?: string;
  readonly artistName?: string;
  readonly siteTitle?: string;
};

/**
 * Verify token response
 */
export type VerifyTokenResponse = {
  readonly valid: boolean;
  readonly user: {
    readonly id: string;
    readonly username: string;
  };
};

/**
 * Logout response
 */
export type LogoutResponse = {
  readonly success: boolean;
  readonly message: string;
};

/**
 * Admin Stats interface
 */
export type AdminStats = {
  readonly images: number;
  readonly pages: number;
  readonly categories: number;
  readonly menuItems: number;
};

/**
 * Blog Post Featured Image interface
 */
export type BlogFeaturedImage = {
  readonly imageId: string;
  readonly url: string;
  readonly alt?: string;
};

/**
 * Blog Post interface
 */
export type BlogPost = {
  readonly _id: string;
  readonly title: string;
  readonly slug: string;
  readonly excerpt?: string;
  readonly content: string;
  readonly featuredImage?: BlogFeaturedImage;
  readonly status: 'draft' | 'published';
  readonly publishedAt?: string;
  readonly order: number;
  readonly pageSlug: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * Blog Posts Response interface
 */
export type BlogPostsResponse = {
  readonly posts: ReadonlyArray<BlogPost>;
  readonly total: number;
  readonly page: number;
  readonly totalPages: number;
};

/**
 * Create axios instance with default configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Enable sending cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor (cookies are sent automatically with withCredentials: true)
 * CSRF token is set in default headers by initCsrf() function
 */
apiClient.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent with withCredentials: true
    // No need to manually add Authorization header
    // CSRF token is already in default headers (set by initCsrf)

    // If sending FormData, remove Content-Type header to let axios set it automatically with boundary
    if (config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for error handling with automatic CSRF retry
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    let apiError: ApiError;

    if (error.response?.data) {
      const data = error.response.data as Record<string, unknown>;
      apiError = {
        message: (data.message as string) || (data.error as string) || 'An error occurred',
        status: error.response?.status,
        errors: data.errors as Record<string, unknown> | undefined,
      };
    } else if (error.request) {
      apiError = {
        message: 'No response from server',
        status: error.response?.status,
      };
    } else {
      apiError = {
        message: error.message,
        status: error.response?.status,
      };
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      // Optionally redirect to login
      // window.location.href = '/admin/login';
    }

    // Handle 403 Forbidden - likely invalid CSRF token
    // Automatically refresh CSRF token and retry once
    if (error.response?.status === 403 && apiError.message.toLowerCase().includes('csrf')) {
      const config = error.config;

      // Check if we've already retried (prevent infinite loops)
      if (!config || (config as { readonly _csrfRetry?: boolean })._csrfRetry) {
        return Promise.reject(apiError);
      }

      try {
        console.info('🔄 CSRF token expired, refreshing and retrying...');

        // Refresh CSRF token
        await initCsrf();

        // Mark this request as retried
        (config as { _csrfRetry?: boolean })._csrfRetry = true;

        // Retry the original request with new CSRF token
        return apiClient.request(config);
      } catch (retryError) {
        console.error('❌ Failed to retry after CSRF refresh:', retryError);
        return Promise.reject(apiError);
      }
    }

    return Promise.reject(apiError);
  }
);

/**
 * Utility Functions
 */

/**
 * Initialize CSRF protection by fetching token from backend
 * Should be called on app initialization (e.g., in App.tsx useEffect)
 */
export const initCsrf = async (): Promise<void> => {
  try {
    const response = await apiClient.get('/csrf-token');
    const { csrfToken } = response.data;

    if (csrfToken) {
      // Set CSRF token in default headers for all future requests
      apiClient.defaults.headers.common['X-CSRF-Token'] = csrfToken;
      console.info('✅ CSRF protection initialized');
    }
  } catch (error) {
    console.error('❌ Failed to initialize CSRF protection:', error);
    // Don't throw - allow app to continue (CSRF will fail on state-changing operations)
  }
};

/**
 * Validate if an image has valid shop data
 * @param image
 */
export const isValidShopItem = (image: Image): boolean => {
  if (!image.shopData) return false;

  const { price, purchaseUrl } = image.shopData;

  return !!(
    price !== undefined &&
    price > 0 &&
    purchaseUrl &&
    purchaseUrl.trim().length > 0 &&
    (purchaseUrl.startsWith('http://') || purchaseUrl.startsWith('https://'))
  );
};

/**
 * Get display text for stock status
 * @param status
 */
export const getStockStatusDisplay = (
  status?: string
): { readonly icon: string; readonly text: string; readonly color: string } | null => {
  switch (status) {
    case 'in_stock':
      return { icon: '✓', text: 'In Stock', color: '#4ade80' };
    case 'out_of_stock':
      return { icon: '✗', text: 'Out of Stock', color: '#f87171' };
    case 'limited':
      return { icon: '⚠', text: 'Limited Stock', color: '#fb923c' };
    default:
      return null;
  }
};

/**
 * API Service Object
 */
const api = {
  /**
   * Health Check
   */
  health: {
    check: async (): Promise<{ readonly status: string; readonly message: string }> => {
      const response = await axios.get('/health');
      return response.data;
    },
  },

  /**
   * Authentication API
   */
  auth: {
    /**
     * Login with username and password
     * Server sets httpOnly cookie automatically
     * @param credentials
     */
    login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
      const response = await apiClient.post('/auth/login', credentials);
      return response.data;
    },

    /**
     * Logout - clears httpOnly cookie on server
     */
    logout: async (): Promise<LogoutResponse> => {
      const response = await apiClient.post('/auth/logout');
      return response.data;
    },

    /**
     * Verify authentication token (from httpOnly cookie)
     */
    verify: async (): Promise<VerifyTokenResponse> => {
      const response = await apiClient.post('/auth/verify');
      return response.data;
    },
  },

  /**
   * Menu API
   */
  menu: {
    getAll: async (): Promise<ReadonlyArray<MenuItem>> => {
      const response = await apiClient.get('/menu');
      // Backend returns { success: true, count: number, data: MenuItem[] }
      return response.data.data ?? [];
    },

    getById: async (id: string): Promise<MenuItem> => {
      const response = await apiClient.get(`/menu/${id}`);
      return response.data;
    },

    create: async (data: Partial<MenuItem>): Promise<MenuItem> => {
      const response = await apiClient.post('/menu', data);
      // Backend returns { success: true, message: string, data: MenuItem }
      return response.data.data;
    },

    update: async (id: string, data: Partial<MenuItem>): Promise<MenuItem> => {
      const response = await apiClient.put(`/menu/${id}`, data);
      // Backend returns { success: true, message: string, data: MenuItem }
      return response.data.data;
    },

    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`/menu/${id}`);
    },

    reorder: async (
      items: ReadonlyArray<{ readonly id: string; readonly order: number }>
    ): Promise<void> => {
      await apiClient.put('/menu/reorder', { items });
    },
  },

  /**
   * Content API
   */
  content: {
    getBySlug: async (slug: string): Promise<PageContent> => {
      const response = await apiClient.get(`/content/${slug}`);
      // Backend returns { success: true, data: PageContent }
      return response.data.data;
    },

    update: async (slug: string, data: Partial<PageContent>): Promise<PageContent> => {
      const response = await apiClient.put(`/content/${slug}`, data);
      // Backend returns { success: true, data: PageContent }
      return response.data.data;
    },
  },

  /**
   * Categories API
   */
  categories: {
    /**
     * Get categories by type (public route)
     * @param type - Category type ('portfolio' or 'shop')
     */
    getByType: async (
      type: 'portfolio' | 'shop'
    ): Promise<{ readonly categories: ReadonlyArray<Category> }> => {
      const response = await apiClient.get(`/categories/${type}`);
      return response.data;
    },

    /**
     * Get all categories (admin route)
     */
    getAll: async (): Promise<{ readonly categories: ReadonlyArray<Category> }> => {
      const response = await apiClient.get('/categories');
      return response.data;
    },

    /**
     * Create new category
     * @param data
     */
    create: async (data: Partial<Category>): Promise<Category> => {
      const response = await apiClient.post('/categories', data);
      return response.data;
    },

    /**
     * Update category
     * @param id
     * @param data
     */
    update: async (id: string, data: Partial<Category>): Promise<Category> => {
      const response = await apiClient.put(`/categories/${id}`, data);
      return response.data;
    },

    /**
     * Delete category
     * @param id
     */
    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`/categories/${id}`);
    },

    /**
     * Reorder categories
     * @param categoryIds - Array of category IDs in desired order
     */
    reorderCategories: async (categoryIds: ReadonlyArray<string>): Promise<void> => {
      await apiClient.put('/categories/reorder', { categoryIds });
    },
  },

  /**
   * Images API
   */
  images: {
    /**
     * Get images by category with pagination
     * @param category - Category slug (e.g., 'all', 'portfolio', 'shop')
     * @param page - Page number (default: 1)
     * @param limit - Items per page (default: 20)
     * @param type - Category type for filtering when category='all' (e.g., 'portfolio', 'shop')
     */
    getImages: async (
      category: string = 'all',
      page: number = 1,
      limit: number = 20,
      type?: 'portfolio' | 'shop'
    ): Promise<{
      readonly images: ReadonlyArray<Image>;
      readonly pagination: {
        readonly page: number;
        readonly limit: number;
        readonly total: number;
        readonly pages: number;
      };
    }> => {
      const response = await apiClient.get(`/images/${category}`, {
        params: { page, limit, type },
      });
      return response.data;
    },

    /**
     * Get all images
     */
    getAll: async (): Promise<ReadonlyArray<Image>> => {
      const response = await apiClient.get('/images/all');
      return response.data.images;
    },

    /**
     * Upload multiple images
     * @param files - Array of files to upload
     * @param category - Category slug
     * @param alt - Alt text for images
     * @param shopData - Shop data if applicable
     */
    uploadImages: async (
      files: ReadonlyArray<File>,
      category?: string,
      alt?: string,
      shopData?: ShopData
    ): Promise<ReadonlyArray<Image>> => {
      const formData = new FormData();

      // Append all files
      files.forEach((file) => formData.append('images', file));

      // Append metadata
      if (category) formData.append('category', category);
      if (alt) formData.append('alt', alt);
      if (shopData) formData.append('shopData', JSON.stringify(shopData));

      // Note: Don't set Content-Type header manually for FormData
      // Axios will automatically set it with the proper boundary
      // Setting it manually would override default headers including X-CSRF-Token
      const response = await apiClient.post('/images/upload', formData);
      return response.data;
    },

    /**
     * Upload single image (legacy)
     * @param file
     * @param categoryId
     * @param metadata
     */
    upload: async (file: File, categoryId: string, metadata?: Partial<Image>): Promise<Image> => {
      const formData = new FormData();
      formData.append('images', file);
      formData.append('category', categoryId);

      if (metadata) {
        Object.entries(metadata).forEach(([key, value]) => {
          if (value !== undefined) {
            formData.append(key, String(value));
          }
        });
      }

      // Note: Don't set Content-Type header manually for FormData
      // Axios will automatically set it with the proper boundary
      // Setting it manually would override default headers including X-CSRF-Token
      const response = await apiClient.post('/images/upload', formData);
      return response.data.images[0];
    },

    /**
     * Update image metadata
     * @param id
     * @param data
     */
    update: async (id: string, data: Partial<Image>): Promise<Image> => {
      const response = await apiClient.put(`/images/${id}`, data);
      return response.data;
    },

    /**
     * Delete image
     * @param id
     */
    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`/images/${id}`);
    },

    /**
     * Reorder images
     * @param imageIds - Array of image IDs in desired order
     */
    reorderImages: async (imageIds: ReadonlyArray<string>): Promise<void> => {
      await apiClient.put('/images/reorder', { imageIds });
    },

    /**
     * Batch update multiple images
     * @param imageIds - Array of image IDs to update
     * @param updates - Updates to apply (category, enabled)
     * @param updates.category
     * @param updates.enabled
     */
    batchUpdate: async (
      imageIds: ReadonlyArray<string>,
      updates: { readonly category?: string; readonly enabled?: boolean }
    ): Promise<{ readonly message: string; readonly modifiedCount: number }> => {
      const response = await apiClient.put('/images/batch', { imageIds, updates });
      return response.data;
    },

    /**
     * Batch delete multiple images
     * @param imageIds - Array of image IDs to delete
     */
    batchDelete: async (
      imageIds: ReadonlyArray<string>
    ): Promise<{ readonly message: string; readonly deletedCount: number }> => {
      const response = await apiClient.delete('/images/batch', { data: { imageIds } });
      return response.data;
    },
  },

  /**
   * Admin API
   */
  admin: {
    getStats: async (): Promise<AdminStats> => {
      const response = await apiClient.get('/admin/stats');
      return response.data;
    },
  },

  /**
   * Site Settings API
   */
  siteSettings: {
    /**
     * Get site settings (artist photo, name, etc.)
     */
    get: async (): Promise<SiteSettings> => {
      const response = await apiClient.get('/site-settings');
      return response.data;
    },

    /**
     * Update site settings (admin only)
     * @param data
     */
    update: async (data: Partial<SiteSettings>): Promise<{ readonly settings: SiteSettings }> => {
      const response = await apiClient.put('/site-settings', data);
      return response.data;
    },
  },

  /**
   * Blog API
   */
  blog: {
    /**
     * Get published posts for a blog page (public)
     * @param pageSlug - The slug of the blog page
     * @param page - Page number (default: 1)
     * @param limit - Items per page (default: 10)
     */
    getByPage: async (
      pageSlug: string,
      page: number = 1,
      limit: number = 10
    ): Promise<BlogPostsResponse> => {
      const response = await apiClient.get(`/blog/page/${pageSlug}`, {
        params: { page, limit },
      });
      return response.data;
    },

    /**
     * Get a single post by slug (public)
     * @param slug - The post slug
     */
    getBySlug: async (slug: string): Promise<BlogPost> => {
      const response = await apiClient.get(`/blog/${slug}`);
      return response.data;
    },

    /**
     * Get all posts for admin (including drafts)
     * @param pageSlug - The slug of the blog page
     */
    getAdminPosts: async (pageSlug: string): Promise<ReadonlyArray<BlogPost>> => {
      const response = await apiClient.get(`/blog/admin/${pageSlug}`);
      return response.data;
    },

    /**
     * Create a new blog post
     * @param data - Post data
     */
    create: async (data: Partial<BlogPost>): Promise<BlogPost> => {
      const response = await apiClient.post('/blog', data);
      return response.data;
    },

    /**
     * Update a blog post
     * @param id - Post ID
     * @param data - Updated post data
     */
    update: async (id: string, data: Partial<BlogPost>): Promise<BlogPost> => {
      const response = await apiClient.put(`/blog/${id}`, data);
      return response.data;
    },

    /**
     * Delete a blog post
     * @param id - Post ID
     */
    delete: async (id: string): Promise<void> => {
      await apiClient.delete(`/blog/${id}`);
    },

    /**
     * Toggle publish status
     * @param id - Post ID
     * @param publish - Whether to publish or unpublish
     */
    publish: async (id: string, publish: boolean): Promise<BlogPost> => {
      const response = await apiClient.put(`/blog/${id}/publish`, { publish });
      return response.data;
    },

    /**
     * Reorder blog posts
     * @param postIds - Array of post IDs in desired order
     */
    reorder: async (postIds: ReadonlyArray<string>): Promise<void> => {
      await apiClient.put('/blog/reorder', { postIds });
    },
  },
};

/**
 * Export the API service
 */
export default api;

/**
 * Export axios instance for custom requests
 */
export { apiClient };
